/**
 * Batch changes on the Users list (SF-4875): give several people a team, or add a role to
 * (or, SF-5076, remove a role from) each of them. The sign-in service has no bulk route, so each person gets the same
 * `PATCH /users/:id` the user detail page sends.
 */
import { authClient } from '../../providers/axios';
import type { RoleAssignment } from './utils';
import { runBatch, type BatchResult } from '../../utils/batch';

export { batchErrorMessage, runBatch } from '../../utils/batch';
export type { BatchFailure, BatchResult } from '../../utils/batch';

/** Sentinel for "clear the team" in the batch picker; the API reads `team: null` as no team. */
export const NO_TEAM = 'none';

/**
 * The person's role list with `role` added, or null when they already hold it.
 * Existing assignments are kept as they are, company lists included, because the
 * PATCH replaces the whole list.
 */
export const withRoleAdded = (
  assignments: RoleAssignment[] | undefined,
  role: string,
): RoleAssignment[] | null => {
  const current = assignments ?? [];
  if (current.some((a) => a.role === role)) return null;
  return [
    ...current.map((a) => ({
      role: a.role,
      customerScope: a.customerScope != null ? [...a.customerScope] : null,
    })),
    { role, customerScope: null },
  ];
};

/**
 * The person's role list without `role`, or null when they don't hold it. The other
 * assignments are kept as they are, company lists included.
 */
export const withRoleRemoved = (
  assignments: RoleAssignment[] | undefined,
  role: string,
): RoleAssignment[] | null => {
  const current = assignments ?? [];
  if (!current.some((a) => a.role === role)) return null;
  return current
    .filter((a) => a.role !== role)
    .map((a) => ({
      role: a.role,
      customerScope: a.customerScope != null ? [...a.customerScope] : null,
    }));
};

/** Sets (or with NO_TEAM clears) the team of every person in `ids`. */
export const setTeamForUsers = (ids: string[], team: string): Promise<BatchResult> =>
  runBatch(ids, async (id) => {
    await authClient.patch(`/users/${id}`, { team: team === NO_TEAM ? null : team });
    return 'updated';
  });

// Reads the person's roles fresh, applies `change`, and writes the whole list back.
const changeRoleForUsers = (
  ids: string[],
  role: string,
  change: (assignments: RoleAssignment[], role: string) => RoleAssignment[] | null,
): Promise<BatchResult> =>
  runBatch(ids, async (id) => {
    const { data } = await authClient.get(`/users/${id}`);
    const user = (data?.user ?? data) as { roleAssignments?: unknown } | undefined;
    // The PATCH replaces the whole list, so writing without a real current list would wipe
    // this person's other roles. The sign-in service always sends the array.
    if (!user || !Array.isArray(user.roleAssignments)) {
      throw new Error("Could not read this person's current roles");
    }
    const next = change(user.roleAssignments as RoleAssignment[], role);
    if (!next) return 'unchanged';
    await authClient.patch(`/users/${id}`, { roleAssignments: next });
    return 'updated';
  });

/**
 * Adds `role` to every person in `ids`. Each person's roles are read fresh just before the
 * write, so a change made since the list loaded is not overwritten.
 */
export const addRoleToUsers = (ids: string[], role: string): Promise<BatchResult> =>
  changeRoleForUsers(ids, role, withRoleAdded);

/**
 * Removes `role` from every person in `ids` who holds it, read fresh just before the write
 * like addRoleToUsers. People without it are left alone.
 */
export const removeRoleFromUsers = (ids: string[], role: string): Promise<BatchResult> =>
  changeRoleForUsers(ids, role, withRoleRemoved);
